import { Component, inject } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { KitLotFilterDialogData, KitLotFilterDialogResult } from '../../models/dialog.model';

/**
 * Dialog that allows the user to choose which kit lots should remain in the study.
 * For the RMQC run type, also collects how many assay rows to seed per selected kit lot.
 */
@Component({
  selector: 'app-kit-lot-filter-dialog',
  templateUrl: './kit-lot-filter-dialog.component.html',
  styleUrl: './kit-lot-filter-dialog.component.scss'
})
export class KitLotFilterDialogComponent {
  readonly dialogRef = inject(MatDialogRef<KitLotFilterDialogComponent>);
  readonly data = inject<KitLotFilterDialogData>(MAT_DIALOG_DATA);

  protected readonly sortedKitLots = [...(this.data?.kitLots ?? [])]
    .sort((a, b) => a.localeCompare(b));

  protected selectedLots = new Set<string>(
    (this.data?.selectedKitLots?.length ? this.data.selectedKitLots : this.data?.kitLots) ?? []
  );

  protected readonly isRmqcRunType = !!this.data?.isRmqcRunType;
  protected numberOfAssays: number | null = this.data?.numberOfAssays ?? 1;

  /** One assay per kit lot is the floor, so there is nothing below it to step down to. */
  protected get canDecreaseAssayCount(): boolean {
    return Math.trunc(Number(this.numberOfAssays)) > 1;
  }

  protected stepAssayCount(delta: number): void {
    const current = Math.trunc(Number(this.numberOfAssays));
    const base = Number.isFinite(current) && current > 0 ? current : 1;
    this.numberOfAssays = Math.max(1, base + delta);
  }

  toggleLot(lot: string, event: MatCheckboxChange): void {
    if (event.checked) {
      this.selectedLots.add(lot);
    } else {
      this.selectedLots.delete(lot);
    }
  }

  selectAll(): void {
    this.sortedKitLots.forEach(lot => this.selectedLots.add(lot));
  }

  clearAll(): void {
    this.selectedLots.clear();
  }

  applySelection(): void {
    if (this.isApplyDisabled()) {
      return;
    }
    const result: KitLotFilterDialogResult = {
      kitLots: Array.from(this.selectedLots),
      ...(this.isRmqcRunType ? { numberOfAssays: this.numberOfAssays ?? 1 } : {})
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  isApplyDisabled(): boolean {
    if (this.selectedLots.size === 0) {
      return true;
    }
    return this.isRmqcRunType && (!this.numberOfAssays || this.numberOfAssays < 1);
  }
}
